import React, { useState } from 'react';
import { Link, NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  TrophyIcon,
  AcademicCapIcon,
  BeakerIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/candidate/dashboard', icon: HomeIcon },
  { name: 'My Tasks', href: '/candidate/dashboard', icon: ClipboardDocumentListIcon },
  { name: 'Game Mode', href: '/game-mode', icon: TrophyIcon },
  { name: 'Learning Paths', href: '/learning', icon: AcademicCapIcon },
  { name: 'Simulations', href: '/simulations', icon: BeakerIcon },
  { name: 'Behavioral Analysis', href: '/behavioral-analysis', icon: ChartBarIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
  { name: 'Support', href: '/support', icon: QuestionMarkCircleIcon },
];

const settingsSubNavigation = [
  { name: 'Profile', href: '/settings/profile', icon: UserIcon },
  { name: 'Notifications', href: '/settings/notifications', icon: BellIcon },
  { name: 'Security', href: '/settings/security', icon: ShieldCheckIcon },
];

const CandidateLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSettingsRoute = location.pathname.startsWith('/settings') || location.pathname === '/profile';

  const renderNavigation = (isMobile = false) => (
    <>
      {navigation.map((item) => {
        const isSettingsItem = item.name === 'Settings';
        const isActive = isSettingsItem ? isSettingsRoute : location.pathname === item.href;
        
        return (
          <div key={item.name}>
            <NavLink
              to={item.href}
              onClick={isMobile ? () => setSidebarOpen(false) : undefined}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                isActive
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon
                className="mr-3 flex-shrink-0 h-6 w-6"
                aria-hidden="true"
              />
              {item.name}
            </NavLink>
            
            {/* Settings sub-navigation */}
            {isSettingsItem && isSettingsRoute && (
              <div className="ml-6 mt-1 space-y-1">
                {settingsSubNavigation.map((subItem) => (
                  <NavLink
                    key={subItem.name}
                    to={subItem.href}
                    onClick={isMobile ? () => setSidebarOpen(false) : undefined}
                    className={({ isActive }) =>
                      `group flex items-center px-2 py-1.5 text-xs font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-800'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                      }`
                    }
                  >
                    <subItem.icon
                      className="mr-2 flex-shrink-0 h-4 w-4"
                      aria-hidden="true"
                    />
                    {subItem.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <Link to="/candidate/dashboard" className="flex items-center">
            <span className="text-xl font-bold text-blue-600">SkillFoundry</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {renderNavigation(true)}
        </nav>
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt="User" 
                  className="h-10 w-10 rounded-full object-cover" 
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {(user?.name || user?.email)?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-700">{user?.name || user?.email || 'User'}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role || 'Candidate'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white shadow-lg">
        <div className="flex items-center h-16 px-4 border-b border-gray-200">
          <Link to="/candidate/dashboard" className="flex items-center">
            <span className="text-xl font-bold text-blue-600">SkillFoundry</span>
          </Link>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {renderNavigation()}
        </nav>
        <div className="flex-shrink-0 p-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt="User" 
                  className="h-10 w-10 rounded-full object-cover" 
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-white font-medium">
                    {(user?.name || user?.email)?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-700">{user?.name || user?.email || 'User'}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role || 'Candidate'}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="mt-3 w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <ArrowRightOnRectangleIcon className="mr-2 h-5 w-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 lg:pl-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-10 flex items-center h-16 px-4 bg-white shadow-sm lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="ml-4 flex-1">
            <h1 className="text-lg font-semibold text-gray-900">Candidate Dashboard</h1>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default CandidateLayout; 